// Tenant-agnostic it-IT label maps for schema enums.
//
// The backend's `[[EXTRACTION]]` JSON round-trips raw enum keys (English
// snake_case) into the UI. Every surface that renders one — the profile
// chip on the recommendation screen, the profile section in Account —
// reads through this module so we don't leak raw keys like
// "anxiety_mild" or "hypertension" into the chat.
//
// Exhaustiveness is enforced at compile time via `Record<GoalKey, string>`
// and `Record<ConditionKey, string>`. Adding an enum value to
// `backend/data/schema.json` without updating this file will fail
// `tsc --noEmit`, which is gated by `npm run typecheck`. This is the
// project's chosen alternative to a runtime test framework (per the
// "no new test infra" constraint of the Phase 2 wave 1 brief).

// Keep these unions in sync with backend/data/schema.json:
//   - GoalKey   ← definitions.Indication.properties.goal.enum
//   - ConditionKey ← definitions.PopulationFlag.enum
//
// Drift detection: backend's `incorposano.domain.schema_enums` reads the
// same source at import time and crashes on schema mismatch; if the
// backend boots, its enum set is correct, and a frontend type error here
// proves the maps diverged.

export type GoalKey =
  | "stress_management"
  | "anxiety_mild"
  | "sleep_quality"
  | "sleep_onset"
  | "energy_fatigue"
  | "cognitive_focus"
  | "mood_support"
  | "immune_winter"
  | "immune_baseline"
  | "respiratory_support"
  | "digestion_bloating"
  | "digestion_reflux"
  | "digestion_transit"
  | "gut_microbiome"
  | "digestion_enzymatic"
  | "joint_pain"
  | "joint_mobility"
  | "muscle_recovery"
  | "cardiovascular_lipids"
  | "cardiovascular_pressure"
  | "glucose_metabolism"
  | "venous_circulation"
  | "skin_aging"
  | "skin_hydration"
  | "hair_nails"
  | "bone_health"
  | "iron_deficiency"
  | "menopause"
  | "premenopause"
  | "prostate_bph"
  | "urinary_tract"
  | "athletic_performance"
  | "weight_management"
  | "liver_detox"
  | "antioxidant_general"
  | "thyroid_support"
  | "libido_vitality";

export type ConditionKey =
  | "pregnancy"
  | "breastfeeding"
  | "pediatric_under_12"
  | "pediatric_12_18"
  | "elderly_over_65"
  | "hypertension"
  | "diabetes_t1"
  | "diabetes_t2"
  | "thyroid_hyper"
  | "thyroid_hypo"
  | "autoimmune_active"
  | "kidney_disease"
  | "liver_disease"
  | "cardiovascular_disease"
  | "bleeding_disorder"
  | "scheduled_surgery_30d"
  | "epilepsy"
  | "depression_treated"
  | "anxiety_treated"
  | "hormone_dependent_cancer"
  | "gallbladder_disease"
  | "kidney_stones"
  | "iron_overload_hemochromatosis"
  | "g6pd_deficiency"
  | "gout"
  | "gerd_ulcer";

export const GOAL_LABELS: Record<GoalKey, string> = {
  stress_management: "Stress",
  anxiety_mild: "Ansia lieve",
  sleep_quality: "Sonno",
  sleep_onset: "Addormentamento",
  energy_fatigue: "Energia",
  cognitive_focus: "Focus",
  mood_support: "Umore",
  immune_winter: "Immunità (inverno)",
  immune_baseline: "Immunità",
  respiratory_support: "Vie respiratorie",
  digestion_bloating: "Gonfiore",
  digestion_reflux: "Reflusso",
  digestion_transit: "Transito",
  gut_microbiome: "Microbiota",
  digestion_enzymatic: "Digestione enzimatica",
  joint_pain: "Dolori articolari",
  joint_mobility: "Mobilità",
  muscle_recovery: "Recupero muscolare",
  cardiovascular_lipids: "Colesterolo",
  cardiovascular_pressure: "Pressione",
  glucose_metabolism: "Glicemia",
  venous_circulation: "Circolazione venosa",
  skin_aging: "Pelle",
  skin_hydration: "Idratazione pelle",
  hair_nails: "Capelli e unghie",
  bone_health: "Ossa",
  iron_deficiency: "Ferro",
  menopause: "Menopausa",
  premenopause: "Premenopausa",
  prostate_bph: "Prostata",
  urinary_tract: "Vie urinarie",
  athletic_performance: "Performance",
  weight_management: "Peso",
  liver_detox: "Fegato",
  antioxidant_general: "Antiossidanti",
  thyroid_support: "Tiroide",
  libido_vitality: "Libido e vitalità",
};

export const CONDITION_LABELS: Record<ConditionKey, string> = {
  pregnancy: "Gravidanza",
  breastfeeding: "Allattamento",
  pediatric_under_12: "Pediatrico (<12)",
  pediatric_12_18: "Adolescente (12–18)",
  elderly_over_65: "Anziano (>65)",
  hypertension: "Ipertensione",
  diabetes_t1: "Diabete tipo 1",
  diabetes_t2: "Diabete tipo 2",
  thyroid_hyper: "Ipertiroidismo",
  thyroid_hypo: "Ipotiroidismo",
  autoimmune_active: "Patologia autoimmune attiva",
  kidney_disease: "Patologia renale",
  liver_disease: "Patologia epatica",
  cardiovascular_disease: "Patologia cardiovascolare",
  bleeding_disorder: "Disturbo emorragico",
  scheduled_surgery_30d: "Intervento programmato (30 gg)",
  epilepsy: "Epilessia",
  depression_treated: "Depressione (in trattamento)",
  anxiety_treated: "Ansia (in trattamento)",
  hormone_dependent_cancer: "Tumore ormono-dipendente",
  gallbladder_disease: "Patologia biliare",
  kidney_stones: "Calcoli renali",
  iron_overload_hemochromatosis: "Emocromatosi",
  g6pd_deficiency: "Deficit G6PD",
  gout: "Gotta",
  gerd_ulcer: "Reflusso / ulcera",
};

// Runtime data isn't strictly typed (the backend payload could carry a
// brand-new enum key the frontend hasn't shipped yet). These helpers do
// the safe lookup with a humanised fallback so the UI never renders raw
// snake_case to the user.
export function goalLabel(key: string): string {
  if (key in GOAL_LABELS) return GOAL_LABELS[key as GoalKey];
  return key.replace(/_/g, " ");
}

export function conditionLabel(key: string): string {
  if (key in CONDITION_LABELS) return CONDITION_LABELS[key as ConditionKey];
  return key.replace(/_/g, " ");
}

export const CLINICAL_CHANGE_FLAG_LABELS: Record<string, string> = {
  new_symptom_undiagnosed: "nuovo sintomo da valutare",
  therapy_discontinuation_self_directed: "terapia interrotta di tua iniziativa",
  dose_change_self_directed: "dose modificata di tua iniziativa",
  therapy_adherence_irregular: "assunzione irregolare della terapia",
};

export function clinicalChangeFlagLabel(flag: string): string {
  return CLINICAL_CHANGE_FLAG_LABELS[flag] ?? flag.replace(/_/g, " ");
}
